import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { User } from '@angular/fire/auth';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CallService, CallData } from '../../services/call.service';

@Component({
  selector: 'app-video-call',
  templateUrl: './video-call.component.html',
  styleUrls: ['./video-call.component.css'],
  standalone: true,
  imports: [
    CommonModule
  ]
})
export class VideoCallComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('localVideo') localVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo') remoteVideoRef!: ElementRef<HTMLVideoElement>;

  @Input() receiverUid: string = '';
  @Input() receiverEmail: string = '';
  @Input() isCallActive: boolean = false;
  @Output() callEnded: EventEmitter<void> = new EventEmitter<void>();

  isMuted: boolean = false;
  isVideoOff: boolean = false;
  currentUser: User | null = null;

  // Call state tracking
  callState: {
    isActive: boolean;
    isOutgoing: boolean;
    isIncoming: boolean;
    callerEmail: string;
    receiverId: string;
    status: string;
  } = {
      isActive: false,
      isOutgoing: false,
      isIncoming: false,
      callerEmail: '',
      receiverId: '',
      status: ''
    };

  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  public remoteStream: MediaStream | null = null;
  private subscriptions: Subscription[] = [];
  private currentCallData: CallData | null = null;

  constructor(
    private authService: AuthService,
    private toastr: ToastrService,
    private callService: CallService
  ) { }

  ngOnInit(): void {
    console.log('VideoCallComponent initialized');

    // Subscribe to user authentication state
    this.subscriptions.push(
      this.authService.userSubject.subscribe(user => {
        this.currentUser = user;
        console.log('Current user:', user?.email);
      })
    );

    // Subscribe to call data
    this.subscriptions.push(
      this.callService.callData$.subscribe(callData => {
        if (callData) {
          this.currentCallData = callData;
          this.handleCallData(callData);
        } else {
          this.resetCallState();
        }
      })
    );
  }

  ngAfterViewInit(): void {

  }

  ngOnDestroy(): void {
    this.cleanup();

    // Clean up all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Sửa handleCallData để xử lý call ended
  private handleCallData(callData: CallData): void {
    console.log('Handling call data:', callData);

    // Kiểm tra nếu call đã kết thúc
    if (callData.status === 'ended' || callData.status === 'rejected') {
      console.log('Call ended, cleaning up...');
      this.handleCallEnded();
      return;
    }

    // Update call state
    this.callState = {
      isActive: callData.status === 'accepted',
      isOutgoing: this.currentUser?.uid === callData.providerUid,
      isIncoming: this.currentUser?.uid === callData.clientUid && callData.status === 'incoming',
      callerEmail: callData.providerEmail || '',
      receiverId: callData.clientUid || '',
      status: callData.status || ''
    };

    console.log('Updated call state:', this.callState);

    // Setup WebRTC if not already done
    if (!this.peerConnection && (this.callState.isIncoming || this.callState.isOutgoing)) {
      this.setupPeerConnection();
      return; // Đợi setup xong rồi mới xử lý tiếp
    }

    // Xử lý theo thứ tự: offer -> answer -> candidates
    if (this.peerConnection) {
      this.processWebRTCData(callData);
    }
  }

  private handleCallEnded(): void {
    console.log('Handling call ended');

    // Cleanup trước
    this.cleanup();
    this.resetCallState();

    // Emit event để parent component xử lý
    setTimeout(() => {
      this.callEnded.emit();
    }, 100); // Delay nhỏ để đảm bảo cleanup hoàn tất
  }

  private async processWebRTCData(callData: CallData): Promise<void> {
    if (!this.peerConnection) return;

    try {
      // 1. Xử lý offer trước (cho incoming calls)
      if (callData.offer && this.callState.isIncoming && !this.peerConnection.remoteDescription) {
        console.log('Processing offer...');
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(callData.offer));
      }

      // 2. Xử lý answer (cho outgoing calls)
      if (callData.answer && this.callState.isOutgoing && !this.peerConnection.remoteDescription) {
        console.log('Processing answer...');
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(callData.answer));
      }

      // 3. Xử lý ICE candidates sau khi đã có remote description
      if (callData.candidates && callData.candidates.length > 0 && this.peerConnection.remoteDescription) {
        console.log('Processing ICE candidates...');
        await this.addIceCandidates(callData.candidates);
      }
    } catch (error) {
      console.error('Error processing WebRTC data:', error);
    }
  }

  private resetCallState(): void {
    this.callState = {
      isActive: false,
      isOutgoing: false,
      isIncoming: false,
      callerEmail: '',
      receiverId: '',
      status: ''
    };

    // Reset UI states
    this.isMuted = false;
    this.isVideoOff = false;
  }

  // Sửa cleanup method
  private cleanup(): void {
    console.log('Cleaning up video call...');

    // Stop all media tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped track:', track.kind);
      });
      this.localStream = null;
    }

    // Clean up peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // Reset remote stream
    this.remoteStream = null;

    // Clear video elements
    if (this.localVideoRef && this.localVideoRef.nativeElement) {
      this.localVideoRef.nativeElement.srcObject = null;
      this.localVideoRef.nativeElement.pause();
    }
    if (this.remoteVideoRef && this.remoteVideoRef.nativeElement) {
      this.remoteVideoRef.nativeElement.srcObject = null;
      this.remoteVideoRef.nativeElement.pause();
    }
  }

  // Sửa setupPeerConnection để tắt echo
  private async setupPeerConnection(): Promise<void> {
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };

    this.peerConnection = new RTCPeerConnection(configuration);

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.currentCallData) {
        console.log('Sending ICE candidate');
        this.callService.updateCallData({
          candidates: [event.candidate.toJSON()]
        }, this.currentCallData.clientUid);
      }
    };

    // Handle remote stream với retry mechanism
    this.peerConnection.ontrack = (event) => {
      console.log('Remote track received:', event.track.kind);
      if (event.streams && event.streams[0]) {
        this.remoteStream = event.streams[0];
        this.displayRemoteStream();
      }
    };

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      console.log('Connection state:', this.peerConnection?.connectionState);
      if (this.peerConnection?.connectionState === 'connected') {
        console.log('WebRTC connection established successfully');
      }
    };

    // Get local media stream với echo cancellation
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Add local tracks to peer connection
      this.localStream.getTracks().forEach(track => {
        if (this.peerConnection && this.localStream) {
          console.log('Adding local track:', track.kind);
          this.peerConnection.addTrack(track, this.localStream);
        }
      });

      // Display local video với muted để tránh echo
      this.displayLocalStream();

      // Create offer if this is an outgoing call
      if (this.callState.isOutgoing) {
        // Đợi một chút để đảm bảo tracks đã được add
        setTimeout(() => {
          this.createOffer();
        }, 500);
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      this.toastr.error('Không thể truy cập camera hoặc microphone', 'Lỗi');
      this.endCall();
    }
  }

  private displayLocalStream(): void {
    if (this.localVideoRef && this.localVideoRef.nativeElement && this.localStream) {
      this.localVideoRef.nativeElement.srcObject = this.localStream;
      this.localVideoRef.nativeElement.muted = true;
      this.localVideoRef.nativeElement.play().catch(e => console.log('Local video play error:', e));
    } else {
      // Nếu chưa có element, thử lại sau 100ms
      setTimeout(() => this.displayLocalStream(), 100);
    }
  }

  private displayRemoteStream(): void {
    if (this.remoteVideoRef && this.remoteVideoRef.nativeElement && this.remoteStream) {
      this.remoteVideoRef.nativeElement.srcObject = this.remoteStream;
      this.remoteVideoRef.nativeElement.muted = false; // Remote stream không muted
      this.remoteVideoRef.nativeElement.play().catch(e => console.log('Remote video play error:', e));
      console.log('Remote video stream displayed');
    } else {
      // Retry sau 100ms nếu element chưa sẵn sàng
      setTimeout(() => {
        this.displayRemoteStream();
      }, 100);
    }
  }

  private async createOffer(): Promise<void> {
    if (!this.peerConnection) return;
    try {
      const offerDescription = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offerDescription);
      console.log('Offer created, sending to clientUid:', this.currentCallData?.clientUid);

      // Gửi offer vào doc của clientUid
      await this.callService.updateCallData({
        offer: offerDescription
      }, this.currentCallData?.clientUid);
    } catch (error) {
      console.error('Error creating offer:', error);
      this.toastr.error('Không thể tạo cuộc gọi', 'Lỗi');
      this.endCall();
    }
  }

  private async handleOffer(offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) return;

    try {
      console.log('Handling offer');
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
      console.log('Remote description set from offer');
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  }

  private async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) return;

    try {
      console.log('Handling answer');
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      console.log('Remote description set from answer');
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  }

  private async addIceCandidates(candidates: RTCIceCandidateInit[]): Promise<void> {
    if (!this.peerConnection) return;

    try {
      for (const candidate of candidates) {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        console.log('ICE candidate added:', candidate);
      }
    } catch (error) {
      console.error('Error adding ICE candidates:', error);
    }
  }

  // Answer an incoming call
  public async answerCall(): Promise<void> {
    if (!this.peerConnection || !this.callState.isIncoming) return;

    try {
      // Set remote description từ offer
      if (this.currentCallData?.offer && !this.peerConnection.remoteDescription) {
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(this.currentCallData.offer));
        console.log('Remote description set from offer');
      }

      // Create answer
      const answerDescription = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answerDescription);
      console.log('Answer created and local description set');

      // Update call data với answer và status
      await this.callService.updateCallData({
        answer: answerDescription,
        status: 'accepted'
      });

      this.toastr.success('Đã kết nối cuộc gọi', 'Thành công');
    } catch (error) {
      console.error('Error answering call:', error);
      this.toastr.error('Không thể trả lời cuộc gọi', 'Lỗi');
      this.endCall();
    }
  }

  // Thêm method để reject call
  public async rejectCall(): Promise<void> {
    if (!this.callState.isIncoming) return;

    try {
      await this.callService.updateCallData({
        status: 'rejected'
      }, this.currentCallData?.clientUid);

      this.cleanup();
      this.resetCallState();
      this.callEnded.emit();
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  }

  // Sửa endCall method để không gọi endCall service nếu đã ended
  public async endCall(): Promise<void> {
    console.log('Ending call manually...');

    // Cleanup local resources
    this.cleanup();
    this.resetCallState();

    // Chỉ update Firestore nếu call chưa ended
    const currentCallData = this.callService.getCurrentCallData();
    if (currentCallData && currentCallData.status !== 'ended') {
      await this.callService.endCall(currentCallData.clientUid);
    }

    // Emit event
    this.callEnded.emit();
  }

  // Toggle mute audio
  public toggleMute(): void {
    if (!this.localStream) return;

    this.localStream.getAudioTracks().forEach(track => {
      track.enabled = !track.enabled;
      this.isMuted = !track.enabled;
    });
  }

  // Toggle video
  public toggleVideo(): void {
    if (!this.localStream) return;

    this.localStream.getVideoTracks().forEach(track => {
      track.enabled = !track.enabled;
      this.isVideoOff = !track.enabled;
    });
  }

  // Switch camera (if multiple cameras are available)
  public async switchCamera(): Promise<void> {
    if (!this.localStream) return;

    // Get current video track
    const currentVideoTrack = this.localStream.getVideoTracks()[0];
    if (!currentVideoTrack) return;

    // Get all video devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    if (videoDevices.length <= 1) {
      this.toastr.info('Không có camera khác để chuyển đổi', 'Thông báo');
      return;
    }

    // Find current device id
    let currentDeviceId = '';
    try {
      const trackSettings = currentVideoTrack.getSettings();
      currentDeviceId = trackSettings.deviceId || '';
    } catch (error) {
      console.error('Error getting track settings:', error);
      return;
    }

    // Find next device
    let nextDeviceIndex = 0;
    const currentIndex = videoDevices.findIndex(device => device.deviceId === currentDeviceId);
    if (currentIndex >= 0 && currentIndex < videoDevices.length - 1) {
      nextDeviceIndex = currentIndex + 1;
    }

    // Get next device id
    const nextDeviceId = videoDevices[nextDeviceIndex].deviceId;

    // Stop current video track
    currentVideoTrack.stop();

    // Get new video track
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: nextDeviceId } },
        audio: false
      });

      const newVideoTrack = newStream.getVideoTracks()[0];

      // Replace track in peer connection
      if (this.peerConnection) {
        const senders = this.peerConnection.getSenders();
        const videoSender = senders.find(sender =>
          sender.track && sender.track.kind === 'video'
        );

        if (videoSender) {
          await videoSender.replaceTrack(newVideoTrack);
        }
      }

      // Replace track in local stream
      this.localStream.removeTrack(currentVideoTrack);
      this.localStream.addTrack(newVideoTrack);

      // Update local video element
      if (this.localVideoRef && this.localVideoRef.nativeElement) {
        this.localVideoRef.nativeElement.srcObject = this.localStream;
      }

      this.toastr.success('Đã chuyển đổi camera', 'Thành công');
    } catch (error) {
      console.error('Error switching camera:', error);
      this.toastr.error('Không thể chuyển đổi camera', 'Lỗi');
    }
  }
}
